import { PurseMovementSchema } from '@/queries/transactions/transactions.types'
import { PaymentStatusSchema } from '@/schemas/PaymentStatusSchema'
import { PaymentItemSchema } from '@/schemas/PaymentItemSchema'
import { z } from 'zod'
import { ApprovalRuleSchema } from '@/app/pages/payment-approval-rules/schema'

// Define the schema for the payment task data
const PaymentTaskSchema = z.object({
  task_id: z.number(),
  payment_id: z.number(),
  student_name: z.array(z.string()),
  applicant_name: z.string(),
  submitted_at: z.string(),
  days_since_submitted: z.number(),
  applicant_email: z.string().email().nullable(),
  global_task_status_id: z.number(),
  payment_type: z.string(),
  payment_type_name: z.string(),
  provider_name: z.string().nullable(),
  total_amount: z.number(),
  comments: z.string().nullable(),
  invoice_quote: z.string().nullable(),
  payment_status: PaymentStatusSchema,
  payment_status_name: z.string(),
  updated_notes: z.null().optional(),
})

// Define a schema for pagination link item
const PaginationLinkSchema = z.object({
  url: z.string().nullable(),
  label: z.string(),
  active: z.boolean(),
})

// Define schema for the complete paginated response
const PaginatedPaymentTasksResponseSchema = z.object({
  current_page: z.number(),
  data: z.array(PaymentTaskSchema),
  first_page_url: z.string(),
  from: z.number(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(PaginationLinkSchema),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.number(),
  prev_page_url: z.string().nullable(),
  to: z.number(),
  total: z.number(),
})

// Define types
type PaymentTask = z.infer<typeof PaymentTaskSchema>
type PaginationLink = z.infer<typeof PaginationLinkSchema>
type PaginatedPaymentTasksResponse = z.infer<
  typeof PaginatedPaymentTasksResponseSchema
>

export {
  PaginatedPaymentTasksResponseSchema,
  PaginationLinkSchema,
  PaymentTaskSchema,
  type PaginatedPaymentTasksResponse,
  type PaginationLink,
  type PaymentTask,
}

// Example usage:
// const validateResponse = (data: unknown) => {
//   return PaginatedPaymentTasksResponseSchema.parse(data);
// };
const PaymentDocumentSchema = z.object({
  id: z.number().nullable(),
  global_spend_document_type_id: z.number().nullable(),
  payment_id: z.number().nullable(),
  path: z.string(),
  created_datetime: z.string().nullable(),
  last_datetime: z.string().nullable(),
})

// const PaymentPurseSchema = z.object({
//   name: z.string().nullable(),
//   amount_to_pay: z.number().nullable(),
// });

export const ServiceFeePayerSchema = z.union([
  z.literal('Program'),
  z.literal('Applicant'),
  z.literal('Provider'),
  z.literal('No fee'),
])

export type ServiceFeePayer = z.infer<typeof ServiceFeePayerSchema>

const ApprovalRulePaymentDetailSchema = ApprovalRuleSchema.pick({
  name: true,
  target_id: true,
  target_name: true,
  target_type_code: true,
  description: true,
})

export const PaymentDetailSchema = z.object({
  payment_id: z.number().nullable(),
  status: PaymentStatusSchema,
  type: z.number(),
  program_id: z.number().nullable(),
  approval_rules: z.array(ApprovalRulePaymentDetailSchema),
  provider_id: z.number().nullable(),
  total_amount: z.number(),
  service_fee: z.number(),
  service_fee_payer: ServiceFeePayerSchema,
  amount_provider: z.number(),
  purse_movements: z.array(PurseMovementSchema),
  transaction_type: z.string(),
  invoice_quote: z.string().nullable(),
  applicant_name: z.string().nullable(),
  applicant_appeal_message: z.string().nullable(),
  students: z.array(
    z.object({
      student_name: z.string(),
    })
  ),
  comments: z.string().nullable(),
  has_appealed: z.number().nullable(),
  last_message: z.string().nullable(),
  applicant_review_comment: z.string().nullable(),
  provider_review_comment: z.string().nullable(),
  tracking_number: z.string().nullable(),
  provider_name: z.string().nullable(),
  created_datetime: z.string().nullable(),
  expense_categories: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      amount: z.number(),
    })
  ), // Can be refined if structure is known
  payment_documents: z.array(PaymentDocumentSchema),
  payment_items: z.array(PaymentItemSchema),
  // purses: z.array(PaymentPurseSchema).nullable(),
})
